import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { semanticColors } from '@/common/styles/colorTokens';
import { typography } from '@/common/styles/typographyTokens';
import FactsheetLogo from '@/common/assets/logo/factsheet_logo.png';

import { 
  changePasswordService, 
  ChangePasswordInfoDto 
} from '@/services/changePasswordService';
import { InisisAuthResult } from '@/components-common/inisis/InisisAuthButton';

import ErrorStep from './components/ErrorStep';
import IdentityVerificationStep from './components/IdentityVerificationStep';
import PasswordChangeStep from './components/PasswordChangeStep';
import CompletionStep from './components/CompletionStep';

/**
 * 비밀번호 변경 페이지 단계
 */
type ChangePasswordStep = 
  | 'loading'       // 토큰 검증 중
  | 'verification'  // 본인인증 단계
  | 'change'        // 비밀번호 변경 단계
  | 'complete'      // 완료
  | 'error';        // 에러 (만료/완료됨)

/**
 * 에러 유형
 */
type ErrorType = 'expired' | 'completed' | 'mismatch' | 'invalid' | 'network';

/**
 * 비밀번호 변경 페이지 컴포넌트
 * 이메일로 발송된 비밀번호 변경 링크를 통해 접근
 */
const ChangePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // URL에서 토큰 추출
  const urlToken = searchParams.get('token');
  
  // 상태 관리
  const [step, setStep] = useState<ChangePasswordStep>('loading');
  const [errorType, setErrorType] = useState<ErrorType | null>(null);
  const [userInfo, setUserInfo] = useState<ChangePasswordInfoDto | null>(null);
  const [authResult, setAuthResult] = useState<InisisAuthResult | null>(null);

  /**
   * 토큰 유효성 검증 및 사용자 정보 로드
   */
  useEffect(() => {
    const validateTokenAndLoadInfo = async () => {
      // 토큰이 없으면 에러
      if (!urlToken) {
        setErrorType('invalid');
        setStep('error');
        return;
      }

      try {
        // 1. 토큰 상태 확인
        const statusResult = await changePasswordService.checkStatus(urlToken);
        
        if (!statusResult) {
          setErrorType('network');
          setStep('error');
          return;
        }

        // 만료된 경우
        if (statusResult.isExpired) {
          setErrorType('expired');
          setStep('error');
          return;
        }

        // 이미 완료된 경우
        if (statusResult.isCompleted) {
          setErrorType('completed');
          setStep('error');
          return;
        }

        // 2. 사용자 정보 조회
        const infoResult = await changePasswordService.getInfo(urlToken);
        
        if (!infoResult) {
          setErrorType('network');
          setStep('error');
          return;
        }

        // 정보 저장 및 본인인증 단계로 이동
        setUserInfo(infoResult);
        setStep('verification');
      } catch (error) {
        console.error('토큰 검증 실패:', error);
        setErrorType('network');
        setStep('error');
      }
    };

    validateTokenAndLoadInfo();
  }, [urlToken]);

  /**
   * 본인인증 성공 처리
   */
  const handleVerificationSuccess = (result: InisisAuthResult) => {
    if (!userInfo) return;

    // 이름과 전화번호 비교
    const nameMatch = userInfo.userName.replace(/\s/g, '') === result.userName.replace(/\s/g, '');
    const apiPhone = userInfo.phone.replace(/[^0-9]/g, '');
    const authPhone = result.userPhone.replace(/[^0-9]/g, '');
    const phoneMatch = apiPhone === authPhone;

    if (nameMatch && phoneMatch) {
      // 인증 결과 저장 및 비밀번호 변경 단계로 이동
      setAuthResult(result);
      setStep('change');
    } else {
      // 본인 불일치
      setErrorType('mismatch');
      setStep('error');
    }
  };

  /**
   * 비밀번호 변경 성공 처리
   */
  const handlePasswordChangeSuccess = () => {
    setStep('complete');
  };

  /**
   * 로그인 페이지로 이동
   */
  const handleGoToLogin = () => {
    navigate('/login');
  };

  /**
   * 단계별 컴포넌트 렌더링
   */
  const renderStep = () => {
    switch (step) {
      case 'loading':
        return (
          <LoadingContainer>
            <SpinnerIcon />
            <LoadingText>확인 중...</LoadingText>
          </LoadingContainer>
        );

      case 'error':
        return (
          <ErrorStep 
            errorType={errorType!} 
            onGoToLogin={handleGoToLogin} 
          />
        );

      case 'verification':
        return (
          <IdentityVerificationStep
            userInfo={userInfo!}
            onSuccess={handleVerificationSuccess}
          />
        );

      case 'change':
        return (
          <PasswordChangeStep
            userInfo={userInfo!}
            authResult={authResult!}
            urlToken={urlToken!}
            onSuccess={handlePasswordChangeSuccess}
          />
        );

      case 'complete':
        return (
          <CompletionStep onGoToLogin={handleGoToLogin} />
        );

      default:
        return null;
    }
  };

  return (
    <Container>
      <PageContainer>
        <ContentSection>
          <ContentBox>
            <Logo src={FactsheetLogo} alt="Factsheet Logo" />
            {renderStep()}
          </ContentBox>
        </ContentSection>
      </PageContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: ${semanticColors.background.primary};
  padding: 16px;
  
  @media (max-width: 768px) {
    padding: 0;
  }
`;

const PageContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  max-width: 600px;
  max-height: 700px;
  background-color: ${semanticColors.background.secondary};
  border-radius: 21px;
  overflow: hidden;
  
  @media (max-height: 800px) {
    max-height: 100%;
    overflow-y: auto;
  }
  
  @media (max-width: 768px) {
    border-radius: 0;
    max-height: none;
    min-height: 100vh;
  }
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 48px 24px;
  
  @media (max-height: 800px) {
    padding: 32px 24px;
    align-items: flex-start;
  }
  
  @media (max-width: 768px) {
    padding: 32px 20px;
    min-height: 100vh;
    align-items: flex-start;
    padding-top: 60px;
  }
`;

const ContentBox = styled.div`
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  
  @media (max-height: 800px) {
    gap: 24px;
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
    gap: 24px;
  }
`;

const Logo = styled.img`  
  width: 124px; 
  height: auto;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 0;
`;

const LoadingText = styled.span`
  ${typography.subtitle.subtitle2}
  color: ${semanticColors.text.secondary};
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinnerIcon = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${semanticColors.border.primary};
  border-top: 4px solid ${semanticColors.background.brand};
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

export default ChangePasswordPage;
